// Que paso al reconciliar un pago, en palabras de operador.
//
// Un solo lugar para la tabla de resultados del boton «Reconciliar pago» y
// para la lista de intentos de pago de la ficha: las dos hablan de los mismos
// pagos y tienen que decir lo mismo. Los `Record` son completos sobre el union
// del backend: un resultado o estado nuevo no compila hasta tener su texto.
//
// La distincion que no se puede perder en pantalla: "no pagó" (ámbar, lo
// resuelve el cliente), "rechazado" (rojo, lo resuelve el cliente con su
// banco), "duplicado" (rojo fuerte, lo resolvemos NOSOTROS devolviendo plata)
// y "Line no respondió" (gris, no dice nada del pago).

import type {
  CheckoutAttempt,
  CheckoutAttemptStatus,
  ReconcileOutcome,
  ReconcileResult,
  ReconcileResultRow,
} from './api';
import { declineLabel } from './charge-state';

/** `critical` = rojo lleno: hay plata del cliente que devolver. */
export type ReconcileTone = 'success' | 'warning' | 'error' | 'critical' | 'neutral';

export interface OutcomeView {
  tone: ReconcileTone;
  /** Texto de la pastilla. */
  label: string;
  /** Que significa y que hacer, en una linea. */
  hint: string;
}

export const OUTCOME_VIEW: Record<ReconcileOutcome, OutcomeView> = {
  activated: {
    tone: 'success',
    label: 'activado ahora',
    hint: 'El pago estaba aprobado y no se había acreditado. El plan quedó activo.',
  },
  already_active: {
    tone: 'success',
    label: 'ya estaba acreditado',
    hint: 'Este pago ya había activado el plan. No hay nada que hacer.',
  },
  token_not_ready: {
    tone: 'warning',
    label: 'sin pagar o en proceso',
    hint: 'Line todavía no lo confirmó. Probá de nuevo en unos minutos.',
  },
  rejected: {
    tone: 'error',
    label: 'rechazado',
    hint: 'Line rechazó este pago: no se cobró. El cliente tiene que intentarlo de nuevo.',
  },
  duplicate: {
    tone: 'critical',
    label: 'cobro duplicado — a reintegrar',
    hint: 'Line aprobó este pago, pero la suscripción ya estaba paga con otro. Hay que devolverlo.',
  },
  provider_unavailable: {
    tone: 'neutral',
    label: 'Line no disponible',
    hint: 'No se pudo consultar a Line. El pago no se perdió: reintentá más tarde.',
  },
  error: {
    tone: 'error',
    label: 'error al consultar',
    hint: 'La consulta falló por un error nuestro. Reintentá; si se repite, avisá a soporte técnico.',
  },
  invalid_payment_id: {
    tone: 'error',
    label: 'ID no válido',
    hint: 'El ID ingresado no se puede usar para esta cuenta.',
  },
};

export interface AttemptView {
  tone: ReconcileTone;
  label: string;
}

export const ATTEMPT_VIEW: Record<CheckoutAttemptStatus, AttemptView> = {
  open: { tone: 'warning', label: 'sin confirmar' },
  accepted: { tone: 'success', label: 'acreditado' },
  rejected: { tone: 'error', label: 'rechazado' },
  abandoned: { tone: 'neutral', label: 'abandonado sin pagar' },
  duplicate: { tone: 'critical', label: 'Cobro duplicado — a reintegrar' },
  refunded: { tone: 'neutral', label: 'Reintegrado' },
};

/** Largo minimo de la referencia de un reintegro (igual que el backend). */
export const REFUND_REFERENCE_MIN = 3;
export const REFUND_REFERENCE_MAX = 500;

/**
 * Qué le falta a la referencia que anota el operador al marcar un reintegro,
 * o null si sirve. Sin referencia no se puede rastrear el reintegro despues.
 */
export function refundReferenceProblem(raw: string): string | null {
  const ref = raw.trim();
  if (ref.length < REFUND_REFERENCE_MIN) {
    return 'Anotá el número de operación del reintegro o un comentario (al menos 3 caracteres).';
  }
  if (ref.length > REFUND_REFERENCE_MAX) return 'La referencia es demasiado larga (máximo 500).';
  return null;
}

// Estados crudos de Line que conocemos. Line puede sumar otros: lo que no este
// aca se muestra tal cual, que es mejor que esconderlo.
const LINE_STATUS_LABEL: Record<string, string> = {
  CREATED: 'formulario abierto, sin pagar',
  PENDING: 'en proceso',
  PROCESSING: 'en proceso',
  ACCEPTED: 'aprobado',
  APPROVED: 'aprobado',
  REJECTED: 'rechazado',
  EXPIRED: 'venció sin pagarse',
  CANCELLED: 'cancelado',
  REFUNDED: 'devuelto',
};

/** Lo que dijo Line, traducido. null = Line no dijo nada (no se lo consulto). */
export function lineStatusLabel(status: string | null | undefined): string | null {
  const raw = status?.trim();
  if (!raw) return null;
  return LINE_STATUS_LABEL[raw.toUpperCase()] ?? raw;
}

/**
 * La linea de explicacion de una fila de resultado. Parte del texto fijo del
 * resultado y lo afina con lo que haya dicho Line o la validacion.
 */
export function outcomeDetail(row: ReconcileResultRow): string {
  const base = OUTCOME_VIEW[row.outcome].hint;
  const message = row.message?.trim();

  switch (row.outcome) {
    case 'token_not_ready':
      // CREATED no es "en proceso": nadie pagó. Decirlo evita esperar en vano.
      return row.line_status?.toUpperCase() === 'CREATED'
        ? 'El cliente abrió el formulario de pago y no lo completó. No se le cobró.'
        : base;
    case 'rejected':
      return message ? `Motivo: ${declineLabel(message)}. No se cobró.` : base;
    case 'invalid_payment_id':
    case 'error':
      return message ? message : base;
    default:
      return base;
  }
}

// --- ID de pago ingresado a mano ------------------------------------------

const LINE_PAYMENT_ID = /^pay_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Saca lo que se pega de mas al copiar del panel de Line (espacios, saltos). */
export function normalizeLinePaymentId(input: string): string {
  return input.trim();
}

/** `pay_` + uuid. El backend valida ademas que sea de este cliente. */
export function isLinePaymentId(input: string): boolean {
  return LINE_PAYMENT_ID.test(normalizeLinePaymentId(input));
}

/** `pay_1a2b3c4d…9f0e`: alcanza para reconocerlo; el completo va al copiar. */
export function shortPaymentId(id: string): string {
  return id.length <= 20 ? id : `${id.slice(0, 12)}…${id.slice(-4)}`;
}

// --- Resumen ---------------------------------------------------------------

export interface ReconcileSummary {
  tone: ReconcileTone;
  text: string;
}

// De lo que mas importa a lo que menos: un duplicado tapa todo lo demas porque
// es lo unico que exige una accion nuestra; un pago acreditado tapa a los
// rechazos de los otros intentos, que ya no cambian nada.
const SUMMARY_ORDER: ReconcileOutcome[] = [
  'duplicate',
  'activated',
  'invalid_payment_id',
  'provider_unavailable',
  'error',
  'already_active',
  'rejected',
  'token_not_ready',
];

const SUMMARY_TEXT: Record<ReconcileOutcome, string> = {
  duplicate: 'Hay un cobro duplicado: Line aprobó un pago de más y hay que reintegrarlo.',
  activated: 'Se activó el plan: había un pago aprobado sin acreditar.',
  invalid_payment_id: 'El ID de pago ingresado no se pudo usar.',
  provider_unavailable: 'Line no respondió. Los pagos no se pierden: reintentá más tarde.',
  error: 'La consulta falló. Reintentá; si se repite, avisá a soporte técnico.',
  already_active: 'El plan ya estaba activo con un pago acreditado. No había nada trabado.',
  rejected: 'No hay ningún pago aprobado: Line rechazó los intentos.',
  token_not_ready: 'No hay ningún pago aprobado: los intentos no se completaron.',
};

/** Una linea arriba de la tabla: lo mas importante de toda la consulta. */
export function reconcileSummary(res: ReconcileResult): ReconcileSummary {
  const present = new Set(res.results.map((r) => r.outcome));
  const top = SUMMARY_ORDER.find((o) => present.has(o));
  if (top) return { tone: OUTCOME_VIEW[top].tone, text: SUMMARY_TEXT[top] };

  if (res.provider_unavailable) {
    return { tone: 'neutral', text: SUMMARY_TEXT.provider_unavailable };
  }
  return {
    tone: 'neutral',
    text: 'No hay intentos de pago de Line para consultar. Si el cliente o Line informan un pago, ingresá su ID.',
  };
}

/** Fecha del intento de una fila de resultado (los resultados no la traen). */
export function attemptFor(
  attempts: CheckoutAttempt[] | undefined,
  paymentId: string,
): CheckoutAttempt | null {
  return attempts?.find((a) => a.payment_id === paymentId) ?? null;
}

/** Intentos que hay que reintegrar. */
export function duplicateAttempts(attempts: CheckoutAttempt[] | undefined): CheckoutAttempt[] {
  return (attempts ?? []).filter((a) => a.status === 'duplicate');
}
